import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Email, Search, Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { groupService } from '../services/groupService';
import { styles } from '../utils/styles';

const getProp = (obj: any, prop: string) =>
  obj[prop] ||
  obj[prop.charAt(0).toUpperCase() + prop.slice(1)] ||
  obj[prop.charAt(0).toLowerCase() + prop.slice(1)];

export const MailingLists: React.FC = () => {
  const navigate = useNavigate();

  const [mailingLists, setMailingLists] = useState<any[]>([]);
  const [filteredLists, setFilteredLists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [newList, setNewList] = useState({
    displayName: '',
    description: '',
    mailNickname: '',
    mailEnabled: true,
    securityEnabled: false,
  });

  useEffect(() => {
    loadMailingLists();
  }, []);

  const filterMailingLists = useCallback(() => {
    let filtered = [...mailingLists];

    if (searchTerm) {
      filtered = filtered.filter((list) => {
        const displayName = getProp(list, 'displayName')?.toLowerCase() || '';
        const description = getProp(list, 'description')?.toLowerCase() || '';
        const mail = getProp(list, 'mail')?.toLowerCase() || '';
        return (
          displayName.includes(searchTerm.toLowerCase()) ||
          description.includes(searchTerm.toLowerCase()) ||
          mail.includes(searchTerm.toLowerCase())
        );
      });
    }

    setFilteredLists(filtered);
  }, [mailingLists, searchTerm]);

  useEffect(() => {
    filterMailingLists();
  }, [filterMailingLists]);

  const loadMailingLists = async () => {
    setLoading(true);
    setError(null);
    try {
      // Request more groups to ensure we get all distribution lists
      const response = await groupService.getGroups({ top: 999 });
      const groups = response?.data?.value || getProp(response, 'data')?.value || [];

      console.log('Total groups fetched:', groups.length);

      // Filter for distribution lists only (mail-enabled, not Unified/M365)
      const lists = groups.filter((group: any) => {
        const mailEnabled = getProp(group, 'mailEnabled');
        const groupTypes = getProp(group, 'groupTypes') || [];
        const mail = getProp(group, 'mail');

        // Include groups that are mail-enabled and not Microsoft 365 groups
        // Distribution lists have mailEnabled=true, groupTypes empty or without 'Unified'
        const isDistributionList = mailEnabled === true && !groupTypes.includes('Unified');

        if (isDistributionList) {
          console.log('Distribution list found:', getProp(group, 'displayName'), mail);
        }

        return isDistributionList;
      });

      console.log('Filtered distribution lists:', lists.length);
      setMailingLists(lists);
    } catch (err: any) {
      setError(err.message || 'Failed to load mailing lists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDialogOpen = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setNewList({
      displayName: '',
      description: '',
      mailNickname: '',
      mailEnabled: true,
      securityEnabled: false,
    });
  };

  const handleNewListChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewList({ ...newList, [e.target.name]: e.target.value });
  };

  const handleCreateList = async () => {
    try {
      await groupService.createGroup({
        ...newList,
        groupTypes: newList.securityEnabled ? [] : ['Unified'],
      });
      handleCreateDialogClose();
      loadMailingLists();
    } catch (err: any) {
      setError(err.message || 'Failed to create mailing list');
    }
  };

  const handleViewDetails = (listId: string) => {
    navigate(`/groups/${listId}`);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Box sx={styles.flexCenter}>
          <Email sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Distribution Lists</Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredLists.length} distribution list{filteredLists.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadMailingLists} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateDialogOpen}>
            Create Distribution List
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={styles.errorAlert}>
          {error}
        </Alert>
      )}

      <Paper sx={styles.filterSection}>
        <TextField
          placeholder="Search distribution lists..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Email Address</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Description</TableCell>
              <TableCell sx={styles.tableHeaderCell} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No distribution lists found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLists.map((list) => {
                const displayName = getProp(list, 'displayName');
                const mail = getProp(list, 'mail');
                const description = getProp(list, 'description');
                const id = getProp(list, 'id');

                return (
                  <TableRow
                    key={id}
                    hover
                    sx={{
                      ...styles.tableRow,
                      cursor: 'pointer',
                    }}
                    onClick={() => handleViewDetails(id)}
                  >
                    <TableCell>
                      <Box sx={styles.flexCenter}>
                        <Email sx={{ ...styles.iconMargin, color: 'primary.main' }} />
                        <Typography variant="body1" fontWeight="medium">
                          {displayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{mail || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Implement edit
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Implement delete
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Distribution List Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Distribution List</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={newList.displayName}
              onChange={handleNewListChange}
              required
            />
            <TextField
              fullWidth
              label="Email Alias"
              name="mailNickname"
              value={newList.mailNickname}
              onChange={handleNewListChange}
              helperText="Will be used as the email address prefix"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={newList.description}
              onChange={handleNewListChange}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateList}
            disabled={!newList.displayName || !newList.mailNickname}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
